import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@gapartners/ui';
import { CaseNotesSaveStatus } from '@/components/contact-center/CaseNotesSaveStatus';
import {
  buildCaseNotesWithAppend,
  getCaseNotesAppendSuffix,
  getCaseNotesSaveEligibility,
} from '@/utils/caseNotes';

const TEXTAREA_SX = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '4px',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  color: 'text.primary',
  bgcolor: 'background.paper',
  resize: 'vertical' as const,
  minHeight: '80px',
  '&:focus': {
    outline: 'none',
    borderColor: 'primary.main',
    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.2)',
  },
};

const SAVED_NOTES_SX = {
  p: 1,
  mb: 1,
  borderRadius: '4px',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'action.hover',
  color: 'text.primary',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  whiteSpace: 'pre-wrap' as const,
  wordBreak: 'break-word' as const,
};

export interface CaseNotesAppendEditorProps {
  savedNotes: string;
  currentNotes: string;
  onNotesChange: (fullNotes: string) => void;
  onDiscard: () => void;
}

export function CaseNotesAppendEditor({
  savedNotes,
  currentNotes,
  onNotesChange,
  onDiscard,
}: CaseNotesAppendEditorProps) {
  const { t } = useTranslation();

  const appendText = useMemo(
    () => getCaseNotesAppendSuffix(savedNotes, currentNotes),
    [savedNotes, currentNotes]
  );

  const notesSaveEligibility = useMemo(
    () => getCaseNotesSaveEligibility(savedNotes, currentNotes),
    [savedNotes, currentNotes]
  );

  const hasChanges = notesSaveEligibility !== 'unchanged';

  return (
    <Box>
      <Typography
        variant="body2"
        component="label"
        fontWeight={600}
        sx={{ mb: 0.5, display: 'block' }}
      >
        {t('components.contactCenter.notes')}:
      </Typography>

      {savedNotes.length > 0 && (
        <Box component="pre" sx={SAVED_NOTES_SX}>
          {savedNotes}
        </Box>
      )}

      <Box
        component="textarea"
        value={appendText}
        onChange={e =>
          onNotesChange(buildCaseNotesWithAppend(savedNotes, e.target.value))
        }
        placeholder={t('components.contactCenter.notesAppendPlaceholder')}
        sx={{
          ...TEXTAREA_SX,
          ...(hasChanges && {
            borderColor: '#ed6c02',
            backgroundColor: 'rgba(237, 108, 2, 0.08)',
          }),
        }}
      />

      <CaseNotesSaveStatus
        lastSaved={savedNotes}
        current={currentNotes}
        onDiscard={onDiscard}
      />
    </Box>
  );
}
